import {myTeammateApi} from '@/api/my-teammate.api.js'
import {usePolling} from '@/composables/use-polling.js'
import {mergeMessages} from '@/helpers/messages.js'
import {ref} from 'vue'

/**
 * Helper composable for teammate messages querying in the teammate chat view.
 * The conversation is resolved server-side from the current user, so there is
 * no enrollment or channel id to pass.
 */
export function useTeammateMessagesQuerying() {

  //Refs
  const conversation = ref(null)
  const messages = ref([])

  //Load messages (conversation metadata comes from the same endpoint)
  const loadMessages = async () => {
    const data = await myTeammateApi.getThread()
    conversation.value = data.conversation
    setMessages(data.messages)
  }

  //Force update messages ref
  const updateMessages = () => {
    messages.value = [...messages.value]
  }

  //Helper to set messages after loading. The teammate API doesn't echo a
  //client correlation id (external_id persistence is deferred to the channel
  //layer — see Teammate::InboundMessageService), so we match an unconfirmed
  //optimistic message to a server message with the same body.
  const setMessages = allMessages => {
    messages.value = mergeMessages(messages.value, allMessages, {
      matchOptimistic: (optimistic, incoming) =>
        incoming.isIncoming && incoming.body === optimistic.body,
    })
  }

  //Poll messages. The assistant reply lands asynchronously (the agent runs on a
  //short server-side debounce), so a tight cadence keeps the chat feeling live
  //without a websocket. The poll is a cheap, current-user-scoped read. Errors
  //on background ticks are swallowed by usePolling — the next tick recovers;
  //errors on the user-initiated initial load are still surfaced by the caller.
  const POLL_INTERVAL_MS = 2000
  const {
    start: startPollingMessages,
    stop: stopPollingMessages,
  } = usePolling(loadMessages, {interval: POLL_INTERVAL_MS})

  //Expose interface
  return {
    conversation,
    messages,
    loadMessages,
    updateMessages,
    startPollingMessages,
    stopPollingMessages,
  }
}
